import type { DepartureModel } from '@snowit/types/departures';

import { isNewEntity } from '@snowit/constants';
import useFirebase from '@snowit/firebase';
import { SchemaNames } from '@snowit/jsonschemas/__names';
import { useStore } from '@snowit/store';
import omitter from '@snowit/utilities/omitter';

import useSchemas from '../useSchemas/useSchemas';

export default () => {
  const firebase = useFirebase();
  const { store, setStore } = useStore();

  const { writeDocument, deleteDocument } = useSchemas();

  const getDepartures = async (): Promise<DepartureModel[]> => {
    try {
      const ref = await firebase.query<DepartureModel>({
        collectionName: 'departures',
        filters: (q) => q.limit(0),
      });
      setStore('departures', {
        list: ref,
        ready: true,
      });
      return ref;
    } catch (error) {
      console.error('departures_get', { error });
      setStore('departures', {
        list: [],
        ready: true,
      });
      return [];
    }
  };

  const getDeparture = async (departureUID: string): Promise<DepartureModel | undefined> => {
    try {
      const doc = await firebase.doc<DepartureModel>({
        collectionName: 'departures',
        id: departureUID,
      });
      if (!doc) throw new Error('no document found');
      setStore('departures', {
        list: store.departures.list.map((it) => (it.uid === doc.uid ? doc : it)),
      });
      return doc;
    } catch (error) {
      console.error({ error });
      return undefined;
    }
  };

  const writeDeparture = async (
    departureUID: string | undefined,
    data: Partial<DepartureModel>,
  ): Promise<string | undefined> => {
    try {
      const id = await writeDocument({
        // todo: @matey delete fb
        // @ts-expect-error
        modelName: SchemaNames.DEPARTURE ?? 'departure',
        id: isNewEntity(departureUID) ? undefined : departureUID,
        attributes: omitter(data),
      });
      await getDepartures();
      return id;
    } catch (error) {
      console.error('writeDeparture', { error });
      return undefined;
    }
  };

  const deleteDeparture = async (departureUID: string): Promise<boolean> => {
    if (isNewEntity(departureUID)) return false;
    try {
      await deleteDocument({
        // todo: @matey delete fb
        // @ts-expect-error
        modelName: SchemaNames.DEPARTURE ?? 'departure',
        id: departureUID,
      });
      await getDepartures();
      return true;
    } catch (error) {
      console.error('deleteDeparture', { error });
      return false;
    }
  };

  return {
    getDepartures,
    getDeparture,
    writeDeparture,
    deleteDeparture,
    departures: store.departures.list,
    ready: store.departures.ready,
  };
};
